import 'amazon-connect-streams';

import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ConnectContext, ConnectContextValue, PendingDisposition } from './ConnectContext';
import { ConnectProviderProps } from './ConnectProvider.props';
import { useConnectActions } from './use-connect-actions';
import { useConnectCCP } from './use-connect-ccp';
import {
  ActiveContact,
  AgentSnapshot,
  CallHistoryEntry,
  ConnectInitState,
  ContactAttributes,
  DirectoryEntry,
  QueueSummary,
  RecordingState,
} from '../../../@types/connect.type';
import { CONNECT_ENABLED, CONNECT_PERMISSION } from '../../../configs/connect';
import { isUserHasPermission } from '../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { clearHistory, readDirectory, readHistory } from '../../../utils/connect-storage';
import { useTabLeader } from '../../../utils/use-tab-leader';

// Mounts the Amazon Connect Streams CCP iframe (offscreen) and exposes agent
// state, the active contact, history, directory, and queue data via
// ConnectContext. Lifecycle:
//   1. Wait for the user-permissions query.
//   2. If user lacks `service.calls.search`, render children only — no
//      iframe, no context (consumers fall back via useConnectOptional).
//   3. Otherwise mount the offscreen container and call connect.core.initCCP
//      exactly once (handled by useConnectCCP).

export const ConnectProvider: React.FC<ConnectProviderProps> = ({ children }) => {
  const { user } = useAuth0();
  const userEmail = (user?.email ?? '').toLowerCase();
  const permissionsQuery = useGetUserSystemPermissions();
  const hasPermission = useMemo(
    () => isUserHasPermission(CONNECT_PERMISSION, permissionsQuery.data ?? null),
    [permissionsQuery.data]
  );
  // Master gate for the whole softphone surface: the integration must be
  // enabled AND the user must hold the permission. Connect is currently
  // hard-disabled (CONNECT_ENABLED === false), so this stays false in
  // production — the CCP iframe never initialises and no connect attempt,
  // login popup, retry, or tab-leader heartbeat is made.
  const isConnectActive = CONNECT_ENABLED && hasPermission;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const agentRef = useRef<connect.Agent | null>(null);
  const contactRef = useRef<connect.Contact | null>(null);

  // Only the leader tab runs the CCP iframe — Amazon Connect supports a
  // single active CCP session per browser. Followers display a duplicate-
  // tab notice instead of attempting to mount the iframe.
  const isTabLeader = useTabLeader(isConnectActive && Boolean(userEmail));

  const [initState, setInitState] = useState<ConnectInitState>({
    status: 'uninitialised',
    errorMessage: null,
  });
  const [agent, setAgent] = useState<AgentSnapshot | null>(null);
  const [contact, setContact] = useState<ActiveContact | null>(null);
  const [attributes, setAttributes] = useState<ContactAttributes>({});
  const [recordingState, setRecordingState] = useState<RecordingState>('unknown');
  const [pendingDisposition, setPendingDisposition] = useState<PendingDisposition | null>(null);
  const [queues, setQueues] = useState<QueueSummary[]>([]);
  const [history, setHistory] = useState<CallHistoryEntry[]>([]);
  const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
  const [isPanelOpen, setPanelOpen] = useState(false);

  // Hydrate persisted state once we know the user. Email is the per-user key
  // — without it we'd risk leaking history between accounts on a shared box.
  useEffect(() => {
    if (!userEmail) return;
    setHistory(readHistory(userEmail));
    setDirectory(readDirectory(userEmail));
  }, [userEmail]);

  useConnectCCP({
    containerRef,
    agentRef,
    contactRef,
    hasPermission: isConnectActive,
    userEmail,
    isTabLeader,
    setInitState,
    setAgent,
    setContact,
    setAttributes,
    setRecordingState,
    setQueues,
    setHistory,
    setPendingDisposition,
  });

  const actions = useConnectActions({
    agentRef,
    contactRef,
    userEmail,
    directory,
    setDirectory,
    setRecordingState,
  });

  const value: ConnectContextValue = useMemo(
    () => ({
      initState,
      agent,
      contact,
      attributes,
      recordingState,
      pendingDisposition,
      history,
      directory,
      queues,
      isPanelOpen,
      setPanelOpen,
      ...actions,
      dismissDisposition: () => setPendingDisposition(null),
      clearCallHistory: () => {
        clearHistory(userEmail);
        setHistory([]);
      },
    }),
    [
      initState,
      agent,
      contact,
      attributes,
      recordingState,
      pendingDisposition,
      history,
      directory,
      queues,
      isPanelOpen,
      actions,
      userEmail,
    ]
  );

  if (!isConnectActive || !userEmail) {
    return <>{children}</>;
  }

  return (
    <ConnectContext.Provider value={value}>
      {/* CCP iframe lives offscreen — softphone audio still works because the
          element is in the DOM. Visibility:hidden would also suppress
          microphone access in some browsers. */}
      <Box
        ref={containerRef}
        aria-hidden="true"
        sx={{
          position: 'fixed',
          left: -9999,
          top: -9999,
          width: 320,
          height: 480,
          pointerEvents: 'none',
        }}
      />
      {children}
    </ConnectContext.Provider>
  );
};
